/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../modules/auth'
import { ToolbarClassic } from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarClassic'
import { BiMessageDetail } from 'react-icons/bi'
import { MdOutlineNotificationsActive } from 'react-icons/md'
import { GoSortAsc, GoSortDesc } from 'react-icons/go'
import { IoDocumentTextOutline } from 'react-icons/io5'
import { KTIcon } from '../../../_metronic/helpers'
import NotificationCard from './NotificationCard'
import { notificationService } from '../../services/notification.service'
import { ChatNotification, StatusNotification, SortDirection } from '../../../types/notificationTypes'

const DashboardPage: FC = () => {
  // const navigate = useNavigate()
  const { auth, currentUser } = useAuth()
  const providerCode = currentUser?.providerCode || ''

  // Chat notifications state
  const [chatNotifications, setChatNotifications] = useState<ChatNotification[]>([])
  const [chatLoading, setChatLoading] = useState(true)
  const [chatSortOrder, setChatSortOrder] = useState<SortDirection>('Desc')
  const [chatSearch, setChatSearch] = useState('')

  // Status notifications state
  const [statusNotifications, setStatusNotifications] = useState<StatusNotification[]>([])
  const [statusLoading, setStatusLoading] = useState(true)
  const [statusSortOrder, setStatusSortOrder] = useState<SortDirection>('Desc')
  const [statusSearch, setStatusSearch] = useState('')

  // Debounced search
  const [chatSearchDebounced, setChatSearchDebounced] = useState('')
  const [statusSearchDebounced, setStatusSearchDebounced] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setChatSearchDebounced(chatSearch), 500)
    return () => clearTimeout(timer)
  }, [chatSearch])

  useEffect(() => {
    const timer = setTimeout(() => setStatusSearchDebounced(statusSearch), 500)
    return () => clearTimeout(timer)
  }, [statusSearch])

  // Fetch chat notifications
  useEffect(() => {
    const fetchChatNotifications = async () => {
      if (!providerCode) return

      setChatLoading(true)
      try {
        const response = await notificationService.getChatNotifications({
          limit: 10,
          claimNumber: chatSearchDebounced || undefined,
          sortDirection: chatSortOrder,
        })

        if (response.isSuccess) {
          setChatNotifications(response.data.items)
        }
      } catch (error) {
        console.error('Error fetching chat notifications:', error)
      } finally {
        setChatLoading(false)
      }
    }

    fetchChatNotifications()
  }, [providerCode, chatSearchDebounced])

  // Fetch status notifications
  useEffect(() => {
    const fetchStatusNotifications = async () => {
      if (!providerCode) return

      setStatusLoading(true)
      try {
        const response = await notificationService.getStatusNotifications({
          limit: 10,
          claimNumber: statusSearchDebounced || undefined,
          sortDirection: statusSortOrder,
        })

        if (response.isSuccess) {
          setStatusNotifications(response.data.items)
        }
      } catch (error) {
        console.error('Error fetching status notifications:', error)
      } finally {
        setStatusLoading(false)
      }
    }

    fetchStatusNotifications()
  }, [providerCode, statusSearchDebounced])

  // Sort notifications
  const sortNotifications = <T extends { createdAt: string }>(
    items: T[],
    order: SortDirection
  ): T[] => {
    return [...items].sort((a, b) => {
      const timeA = new Date(`${a.createdAt}Z`).getTime()
      const timeB = new Date(`${b.createdAt}Z`).getTime()
      return order === 'Desc' ? timeB - timeA : timeA - timeB
    })
  }

  const sortedChatNotifications = sortNotifications(chatNotifications, chatSortOrder)
  const sortedStatusNotifications = sortNotifications(statusNotifications, statusSortOrder)

  const renderChatSection = () => {
    if (chatLoading) {
      return (
        <div className='d-flex justify-content-center w-100 py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )
    }

    if (sortedChatNotifications.length === 0) {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center w-100 py-10'>
          <KTIcon iconName='message-text-2' className='fs-3x text-gray-400 mb-4' />
          <p className='text-gray-500'>No chat notifications</p>
        </div>
      )
    }

    return sortedChatNotifications.map((notification, index) => (
      <div key={`chat-${notification.claimNumber}-${index}`} className='me-4'>
        <NotificationCard notification={notification} type='chat' index={index} />
      </div>
    ))
  }

  const renderStatusSection = () => {
    if (statusLoading) {
      return (
        <div className='d-flex justify-content-center w-100 py-5'>
          <div className='spinner-border text-warning' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )
    }

    if (sortedStatusNotifications.length === 0) {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center w-100 py-10'>
          <KTIcon iconName='notification-status' className='fs-3x text-gray-400 mb-4' />
          <p className='text-gray-500'>No status notifications</p>
        </div>
      )
    }

    return sortedStatusNotifications.map((notification, index) => (
      <div key={`status-${notification.claimNumber}-${index}`} className='me-4'>
        <NotificationCard notification={notification} type='status' index={index} />
      </div>
    ))
  }

  return (
    <>
      <div className='container-fluid px-5 py-4'>
        {/* Download Forms Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <h3 className='fw-bolder fs-2 mb-4'>Unduh Formulir</h3>
            <div className='row g-4'>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/Format BCP_LEMBAR MEDIS AWAL.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  LMA
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/Form Kronologis Kecelakaan.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  LEMBAR KRONOLOGIS
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/SURAT PERNYATAAN PERAWATAN.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2 text-center'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  <span className='text-wrap'>SURAT PERNYATAAN PERAWATAN</span>
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/SURAT PERNYATAAN PULANG TANPA MENUNGGU JAMINAN AKHIR.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2 text-center'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  <span className='text-wrap'>SP PULANG TANPA MENUNGGU JAMINAN AKHIR</span>
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/SURAT PERSETUJUAN DAN PERNYATAAN UNTUK PROSES PENELUSURAN.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2 text-center'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  <span className='text-wrap'>SP PROSES PENELUSURAN</span>
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/Form INFORMASI KETERSEDIAAN KAMAR PERAWATAN.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2 text-center'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  <span className='text-wrap'>INFORMASI KETERSEDIAAN KAMAR</span>
                </a>
              </div>
              <div className='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto'>
                <a
                  href='/documents/Panduan Penggunaan Provider Portal AFI Rent System 0.0.3.pdf'
                  className='btn btn-light-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2 text-center'
                  download
                >
                  <IoDocumentTextOutline size={24} />
                  <span className='text-wrap'>PANDUAN PENGGUNAAN PROVIDER PORTAL</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Header Section */}
        <div className='d-flex justify-content-between align-items-center mb-6'>
          <h2 className='fw-bolder fs-2 m-0'>Beranda</h2>
          <div style={{ marginTop: '0.25rem' }}>
            <ToolbarClassic claimType='INPATIENT' onModalClose={() => { }} />
          </div>
        </div>

        {/* Chat Notifications Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <div className='d-flex flex-wrap align-items-center justify-content-between mb-6 gap-3'>
              <div className='d-flex align-items-center'>
                <BiMessageDetail className='text-primary me-4' size={24} />
                <h3 className='fw-bolder fs-2 m-0'>Chat Messages</h3>
                {/* <span className='fs-18 opacity-75 ps-4 mt-1'>10 Latest Update</span> */}
              </div>
              <div className='d-flex align-items-center gap-3'>
                <span className="badge border border-primary text-primary">
                  {sortedStatusNotifications.length} Latest Update
                </span>
                {/* Search Input */}
                {/* <div className='position-relative'>
                  <KTIcon
                    iconName='magnifier'
                    className='fs-4 position-absolute top-50 translate-middle-y ms-3'
                  />
                  <input
                    type='text'
                    className='form-control form-control-sm form-control-solid ps-10'
                    placeholder='Search claim...'
                    value={chatSearch}
                    onChange={(e) => setChatSearch(e.target.value)}
                    style={{minWidth: '180px'}}
                  />
                </div> */}
                {/* Sort Button */}
                {/* <button
                  className='btn btn-sm btn-light-primary d-flex align-items-center gap-2'
                  onClick={() => setChatSortOrder((prev) => (prev === 'Desc' ? 'Asc' : 'Desc'))}
                >
                  {chatSortOrder === 'Desc' ? (
                    <>
                      <GoSortDesc size={16} /> Latest
                    </>
                  ) : (
                    <>
                      <GoSortAsc size={16} /> Oldest
                    </>
                  )}
                </button>*/}
              </div>
            </div>

            <div className='d-flex overflow-auto pb-3 pt-3' style={{ scrollbarWidth: 'thin' }}>
              {renderChatSection()}
            </div>
          </div>
        </div>

        {/* Status Notifications Section */}
        <div className='card shadow-sm mb-6'>
          <div className='card-body p-6'>
            <div className='d-flex flex-wrap align-items-center justify-content-between mb-6 gap-3'>
              <div className='d-flex align-items-center'>
                <MdOutlineNotificationsActive className='text-warning me-4' size={24} />
                <h3 className='fw-bolder fs-2 m-0'>Status Updates</h3>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <span className="badge border border-warning text-warning">
                  {sortedStatusNotifications.length} Latest Update
                </span>
                {/* Search Input */}
                {/* <div className='position-relative'>
                  <KTIcon
                    iconName='magnifier'
                    className='fs-4 position-absolute top-50 translate-middle-y ms-3'
                  />
                  <input
                    type='text'
                    className='form-control form-control-sm form-control-solid ps-10'
                    placeholder='Search claim...'
                    value={statusSearch}
                    onChange={(e) => setStatusSearch(e.target.value)}
                    style={{minWidth: '180px'}}
                  />
                </div> */}
                {/* Sort Button */}
                {/* <button
                  className='btn btn-sm btn-light-warning d-flex align-items-center gap-2'
                  onClick={() => setStatusSortOrder((prev) => (prev === 'Desc' ? 'Asc' : 'Desc'))}
                >
                  {statusSortOrder === 'Desc' ? (
                    <>
                      <GoSortDesc size={16} /> Latest
                    </>
                  ) : (
                    <>
                      <GoSortAsc size={16} /> Oldest
                    </>
                  )}
                </button> */}
              </div>
            </div>

            <div className='d-flex overflow-auto pb-3' style={{ scrollbarWidth: 'thin' }}>
              {renderStatusSection()}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const DashboardWrapper: FC = () => {
  return <DashboardPage />
}

export { DashboardWrapper }
